import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import API_ENDPOINTS from "../config/api";

const COLUMN_MIN = 80;
const COLUMN_MAX = 800;
const DEFAULT_WIDTHS = [140, 200, 320, 180, 220, 380]; // Date, Full Name, Email, Phone, Buyer, Lead Type

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const TilesSection = styled.section`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const ActivityBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const GoBack = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  color: #013566;
  margin-top: 2rem;
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;

  h2 {
    font-size: 1.8rem;
  }
`;

const TileHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: stretch;
  background: #f8f9fa;
  padding: 12px 0;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #eee;
`;

const ResizableCell = styled.div<{ $width: number }>`
  flex: 0 0 ${(p) => p.$width}px;
  min-width: 60px;
  max-width: 600px;
  padding: 0 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #013566;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const RESIZE_HANDLE_WIDTH = 6;

const ResizeHandle = styled.div`
  flex: 0 0 ${RESIZE_HANDLE_WIDTH}px;
  cursor: col-resize;
  background: transparent;
  position: relative;
  user-select: none;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 2px;
    width: 2px;
    background: #ccc;
    border-radius: 1px;
  }

  &:hover::after {
    background: #013566;
    width: 3px;
    left: 1.5px;
  }
`;

const ColumnSpacer = styled.div`
  flex: 0 0 ${RESIZE_HANDLE_WIDTH}px;
`;

const TileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  button {
    padding: 0.5rem;
    border: none;
    outline: none;
    border-radius: 12px;
    background-color: #013566;
    color: white;
  }
`;

const TileRow = styled.div`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #eee;
  padding: 12px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 12px 0;
    margin: 0 -8px;
    padding-left: 8px;
    padding-right: 8px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TileElement = styled.div<{ $width: number }>`
  flex: 0 0 ${(p) => p.$width}px;
  min-width: 60px;
  max-width: 600px;
  padding: 0 16px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TileCount = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  color: #013566;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 16px;
    color: #013566;
  }
`;

const CampaignIdSelect = styled.div`
  margin-bottom: 1.5rem;

  p {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #013566;
  }

  select {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: white;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 200px;

    &:hover {
      border-color: #013566;
    }

    &:focus {
      outline: none;
      border-color: #013566;
      box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
`;

const PaginationButton = styled.button<{ $disabled?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background: ${(props) => (props.$disabled ? "#f8f9fa" : "#013566")};
  color: ${(props) => (props.$disabled ? "#6c757d" : "white")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #012a4a;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const ExpandableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border: 1px solid #eee;
  border-top: none;
  padding: 1rem;
  background: #fafbfc;
  border-radius: 0 0 8px 8px;
  margin-top: -1px;
`;

const ExpandableRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  p {
    width: 250px;
    margin: 0;
  }
`;

const SearchForm = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const SearchButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background-color: #013566;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #012a4a;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ReportButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background-color: #198754;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #157347;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  width: 480px;
  max-width: 95%;
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  h2 {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 700;
    color: #013566;
  }
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #013566;
  }
`;

const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const ModalLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #013566;
`;

const ModalInput = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const ModalSelect = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
  background: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const SkeletonRow = styled.div`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #eee;
  padding: 12px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const SkeletonElement = styled.div`
  height: 20px;
  flex-shrink: 0;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const Leads = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [columnWidths, setColumnWidths] = useState<number[]>(() => [
    ...DEFAULT_WIDTHS,
  ]);
  const [resizingColumn, setResizingColumn] = useState<number | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [reportClientId, setReportClientId] = useState<string>("");
  const [clients, setClients] = useState<any[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const itemsPerPage = 10;

  const handleResizeStart = useCallback((colIndex: number) => {
    setResizingColumn(colIndex);
  }, []);

  useEffect(() => {
    if (resizingColumn === null) return;
    const onMove = (e: MouseEvent) => {
      setColumnWidths((prev) => {
        const next = [...prev];
        const w = next[resizingColumn] + e.movementX;
        next[resizingColumn] = Math.min(COLUMN_MAX, Math.max(COLUMN_MIN, w));
        return next;
      });
    };
    const onUp = () => setResizingColumn(null);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [resizingColumn]);

  const fetchClients = useCallback(async () => {
    if (!token) return;
    try {
      setClientsLoading(true);
      const response = await axios.get(API_ENDPOINTS.CLIENTS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClients(response.data?.data || response.data || []);
    } catch (error) {
      console.error("Error fetching clients for report:", error);
    } finally {
      setClientsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (showReportModal && clients.length === 0 && !clientsLoading) {
      fetchClients();
    }
  }, [showReportModal, clients.length, clientsLoading, fetchClients]);

  const handleGenerateReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reportStartDate || !reportEndDate) {
      alert("Please select a start date and end date.");
      return;
    }

    try {
      setGeneratingReport(true);
      const params: Record<string, string> = {
        start_date: reportStartDate,
        end_date: reportEndDate,
      };
      if (reportClientId) {
        params.buyer_id = reportClientId;
      }
      const response = await axios.get(API_ENDPOINTS.LEADS_EXPORT, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeStart = reportStartDate || "start";
      const safeEnd = reportEndDate || "end";
      link.setAttribute(
        "download",
        `leads-report-${safeStart}-to-${safeEnd}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowReportModal(false);
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Failed to generate report. Please try again.");
    } finally {
      setGeneratingReport(false);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const fetchLeads = useCallback(
    async (page: number) => {
      if (!token) return;
      try {
        setLoading(true);
        const search = debouncedSearchQuery.trim();
        const response = await axios.get(API_ENDPOINTS.LEADS, {
          params: {
            page,
            per_page: itemsPerPage,
            ...(search ? { search_name: search } : {}),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setLeads(response.data?.data || []);
        setCurrentPage(response.data?.current_page ?? page);
        setTotalPages(response.data?.last_page ?? 1);
      } catch (error) {
        console.error("Error fetching leads:", error);
      } finally {
        setLoading(false);
      }
    },
    [token, itemsPerPage, debouncedSearchQuery],
  );

  useEffect(() => {
    fetchLeads(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchQuery, fetchLeads]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    // When searching, keep UI consistent by jumping to page 1 (API paginated)
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  // No client-side slicing now: API already returns 10 per page
  const currentLeads = leads;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  console.log(leads);
  return (
    <Container>
      <PageTitle>Lead Management</PageTitle>

      <SearchForm>
        <SearchInput
          type="text"
          placeholder="Search by name, email, phone, transaction id or lead id..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: 1, maxWidth: "400px" }}
        />
        <ReportButton type="button" onClick={() => setShowReportModal(true)}>
          Generate report
        </ReportButton>
      </SearchForm>

      <TilesSection>
        <Tile>
          <TileHeader>
            <ResizableCell $width={columnWidths[0]}>
              <h2>Date</h2>
            </ResizableCell>
            <ResizeHandle onMouseDown={() => handleResizeStart(0)} />
            <ResizableCell $width={columnWidths[1]}>
              <h2>Full Name</h2>
            </ResizableCell>
            <ResizeHandle onMouseDown={() => handleResizeStart(1)} />
            <ResizableCell $width={columnWidths[2]}>
              <h2>Email</h2>
            </ResizableCell>
            <ResizeHandle onMouseDown={() => handleResizeStart(2)} />
            <ResizableCell $width={columnWidths[3]}>
              <h2>Phone</h2>
            </ResizableCell>
            <ResizeHandle onMouseDown={() => handleResizeStart(3)} />
            <ResizableCell $width={columnWidths[4]}>
              <h2>Buyer</h2>
            </ResizableCell>
            <ResizeHandle onMouseDown={() => handleResizeStart(4)} />
            <ResizableCell $width={columnWidths[5]}>
              <h2>Lead Type</h2>
            </ResizableCell>
          </TileHeader>

          <TileSection>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <SkeletonRow key={index}>
                  <SkeletonElement style={{ width: columnWidths[0] }} />
                  <ColumnSpacer />
                  <SkeletonElement style={{ width: columnWidths[1] }} />
                  <ColumnSpacer />
                  <SkeletonElement style={{ width: columnWidths[2] }} />
                  <ColumnSpacer />
                  <SkeletonElement style={{ width: columnWidths[3] }} />
                  <ColumnSpacer />
                  <SkeletonElement style={{ width: columnWidths[4] }} />
                  <ColumnSpacer />
                  <SkeletonElement style={{ width: columnWidths[5] }} />
                </SkeletonRow>
              ))
            ) : currentLeads.length > 0 ? (
              currentLeads.map((item, index) => (
                <TileRow key={index}>
                  <TileElement $width={columnWidths[0]}>
                    {item?.created_at ? formatDate(item?.created_at) : "N/A"}
                  </TileElement>
                  <ColumnSpacer />
                  <TileElement $width={columnWidths[1]}>
                    {item?.first_name ? item?.first_name : "N/A"}{" "}
                    {item?.last_name ? item?.last_name : "N/A"}
                  </TileElement>
                  <ColumnSpacer />
                  <TileElement
                    $width={columnWidths[2]}
                    style={{
                      textDecoration: "underline",
                      color: "#013566",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(`/leads/${item?.id}`)}
                  >
                    {item?.email ? item?.email : "N/A"}
                  </TileElement>
                  <ColumnSpacer />
                  <TileElement $width={columnWidths[3]}>
                    {item?.phone ? item?.phone : "N/A"}
                  </TileElement>
                  <ColumnSpacer />
                  <TileElement $width={columnWidths[4]}>
                    {item?.client?.name ? item?.client?.name : "N/A"}
                  </TileElement>
                  <ColumnSpacer />
                  <TileElement $width={columnWidths[5]}>
                    {item?.lead_type ? item.lead_type : "N/A"}
                  </TileElement>
                </TileRow>
              ))
            ) : (
              <TileRow>
                <TileElement
                  $width={columnWidths[0]}
                  style={{ flex: 1, maxWidth: "none", textAlign: "center" }}
                >
                  No leads found
                </TileElement>
              </TileRow>
            )}
          </TileSection>
        </Tile>
      </TilesSection>

      {!loading && leads.length > 0 && totalPages > 1 && (
        <PaginationContainer>
          <PaginationButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            $disabled={currentPage === 1}
          >
            Previous
          </PaginationButton>
          <span style={{ color: "#013566", fontWeight: 500 }}>
            Page {currentPage} of {totalPages}
          </span>
          <PaginationButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            $disabled={currentPage === totalPages}
          >
            Next
          </PaginationButton>
        </PaginationContainer>
      )}

      {showReportModal && (
        <ModalOverlay
          onClick={() => {
            if (!generatingReport) {
              setShowReportModal(false);
            }
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Generate Leads Report</h2>
              <CloseButton
                type="button"
                onClick={() => !generatingReport && setShowReportModal(false)}
              >
                ×
              </CloseButton>
            </ModalHeader>
            <form onSubmit={handleGenerateReport}>
              <ModalField>
                <ModalLabel htmlFor="reportStartDate">Start date *</ModalLabel>
                <ModalInput
                  id="reportStartDate"
                  type="date"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  required
                />
              </ModalField>
              <ModalField>
                <ModalLabel htmlFor="reportEndDate">End date *</ModalLabel>
                <ModalInput
                  id="reportEndDate"
                  type="date"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  required
                />
              </ModalField>
              <ModalField>
                <ModalLabel htmlFor="reportClient">
                  Client (optional)
                </ModalLabel>
                <ModalSelect
                  id="reportClient"
                  value={reportClientId}
                  onChange={(e) => setReportClientId(e.target.value)}
                  disabled={clientsLoading}
                >
                  <option value="">Select client</option>
                  {clients.map((client: any) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </ModalSelect>
              </ModalField>
              <ModalActions>
                <ReportButton
                  type="submit"
                  disabled={generatingReport || clientsLoading}
                >
                  {generatingReport ? "Generating..." : "Generate CSV"}
                </ReportButton>
              </ModalActions>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Leads;
